import React, { useState, useMemo, useEffect, useRef } from "react"
import { useSearch, useNavigate } from "@tanstack/react-router"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"

import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { Search, ListChecks, ChevronLeft, ChevronRight, ArrowLeft, Printer } from "lucide-react"
import { DocumentViewer } from "@/components/DocumentViewer"
import type { DocumentSummary, DocumentDetail } from "@/lib/types"

interface ConditionEntry {
  crId: number
  crTitle: string
  crSlug: string
  crTags: string[]
  workflowSlug: string
  workflowTitle: string
}

interface WorkflowsListViewProps {
  docs: DocumentSummary[]
  isLoading: boolean
  useDocumentDetail: (
    slug: string,
    crSlug?: string
  ) => { data: DocumentDetail | undefined; isLoading: boolean }
  // Builds the PDF download URL; omit to hide all download affordances
  pdfUrl?: (workflowSlug: string, crSlug: string, activityId?: number) => string
}

export function WorkflowsListView({
  docs,
  isLoading,
  useDocumentDetail,
  pdfUrl,
}: WorkflowsListViewProps) {
  const [selected, setSelected] = useState<ConditionEntry | null>(null)
  const [activeActivityId, setActiveActivityId] = useState<number | undefined>()
  const [search, setSearch] = useState("")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const routeSearch = useSearch({ strict: false }) as
    | Record<string, string>
    | undefined
  const navigate = useNavigate()
  const pendingHashRef = useRef<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Fetch document detail when a condition is selected
  const { data: detailDoc, isLoading: detailLoading } = useDocumentDetail(
    selected?.workflowSlug ?? "",
    selected?.crSlug
  )

  useEffect(() => {
    setActiveActivityId(undefined)
  }, [selected?.workflowSlug, selected?.crSlug])

  // Scroll content to top whenever the displayed activity changes
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0)
  }, [activeActivityId])

  // Flatten all conditions from all workflow templates
  const allConditions = useMemo(() => {
    const entries: ConditionEntry[] = []
    for (const doc of docs) {
      for (const cr of doc.clinical_reviews ?? []) {
        entries.push({
          crId: cr.id,
          crTitle: cr.title,
          crSlug: cr.slug,
          crTags: cr.keywords ?? [],
          workflowSlug: doc.slug,
          workflowTitle: doc.title,
        })
      }
    }
    return entries
  }, [docs])

  // Auto-select condition when navigating back from a workflow module
  useEffect(() => {
    if (selected || !routeSearch?.workflow) return
    const match = allConditions.find(
      (c) =>
        c.workflowSlug === routeSearch.workflow &&
        (!routeSearch.clinical_review ||
          c.crSlug === routeSearch.clinical_review)
    )
    if (match) setSelected(match)
  }, [allConditions, routeSearch, selected])

  // Auto-select activity from URL param once detail doc is loaded
  useEffect(() => {
    if (routeSearch?.activity && detailDoc?.activities) {
      const activityId = Number(routeSearch.activity)
      if (detailDoc.activities.some((a) => a.id === activityId)) {
        setActiveActivityId(activityId)
      }
    }
  }, [routeSearch?.activity, detailDoc])

  // Deep-link: resolve #section-{key} hash to the owning workflow + condition
  const clinicalReviewParam = routeSearch?.clinical_review
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith("#section-") || selected || allConditions.length === 0) return
    const sectionKey = hash.replace("#section-", "")
    // Find the workflow doc whose section_keys include this key
    const matchDoc = docs.find((d) => d.section_keys?.includes(sectionKey))
    if (!matchDoc) return
    // A workflow template can have multiple clinical review variants (e.g. Asthma, Atopic Dermatitis)
    const matchCondition = allConditions.find(
      (c) =>
        c.workflowSlug === matchDoc.slug &&
        (!clinicalReviewParam || c.crSlug === clinicalReviewParam)
    )
    if (matchCondition) {
      pendingHashRef.current = sectionKey
      setSelected(matchCondition)
    }
  }, [allConditions, docs, selected, clinicalReviewParam])

  // Deep-link: resolve pending hash to the owning activity once detail loads
  useEffect(() => {
    if (!pendingHashRef.current || !detailDoc?.activities) return
    const sectionKey = pendingHashRef.current
    const match = detailDoc.activities.find((a) =>
      a.sections.some((s) => s.section_key === sectionKey)
    )
    if (match) {
      setActiveActivityId(match.id)
      pendingHashRef.current = null
    }
  }, [detailDoc])

  // Deep-link: scroll to the section after content renders
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith("#section-") || !activeActivityId || !detailDoc) return
    // Double rAF: first waits for React commit, second waits for paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" })
      })
    })
  }, [activeActivityId, detailDoc])

  const filtered = useMemo(() => {
    if (!search.trim()) return allConditions
    const q = search.toLowerCase()
    return allConditions.filter((c) => {
      if (c.crTitle.toLowerCase().includes(q)) return true
      if (c.crTags.some((tag) => tag.toLowerCase().includes(q))) return true
      const wfDoc = docs.find((d) => d.slug === c.workflowSlug)
      if (!wfDoc) return false
      return (
        wfDoc.title.toLowerCase().includes(q) ||
        wfDoc.keywords?.some((kw) => kw.toLowerCase().includes(q)) ||
        wfDoc.compliance_tags?.some((tag) => tag.toLowerCase().includes(q))
      )
    })
  }, [allConditions, search, docs])

  // Group filtered conditions by workflow template for display
  const groupedFiltered = useMemo(() => {
    const map = new Map<
      string,
      { doc: DocumentSummary; conditions: ConditionEntry[] }
    >()
    for (const entry of filtered) {
      const wfDoc = docs.find((d) => d.slug === entry.workflowSlug)
      if (!wfDoc) continue
      if (!map.has(entry.workflowSlug)) {
        map.set(entry.workflowSlug, { doc: wfDoc, conditions: [] })
      }
      map.get(entry.workflowSlug)!.conditions.push(entry)
    }
    const groups = Array.from(map.values())
    groups.sort((a, b) => a.doc.title.localeCompare(b.doc.title, undefined, { sensitivity: 'base' }))
    for (const g of groups) {
      g.conditions.sort((a, b) => a.crTitle.localeCompare(b.crTitle, undefined, { sensitivity: 'base' }))
    }
    return groups
  }, [filtered, docs])

  const handleBack = () => {
    setSelected(null)
    setActiveActivityId(undefined)
    navigate({ to: ".", search: {}, replace: true })
  }

  const handleSelectCondition = (entry: ConditionEntry) => {
    setSelected(entry)
    setActiveActivityId(undefined)
    navigate({
      to: ".",
      search: { workflow: entry.workflowSlug, clinical_review: entry.crSlug },
      replace: true,
    })
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  // Detail mode: module sidebar + document viewer
  if (selected) {
    const selectedDoc = docs.find((d) => d.slug === selected.workflowSlug)

    return (
      <div className="flex h-full">
        {/* Collapsible module sidebar */}
        <div className={`flex shrink-0 border-r bg-muted/30 ${sidebarCollapsed ? "" : "w-72"}`}>
          {!sidebarCollapsed && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="px-3 pt-3">
                <Button variant="ghost" size="sm" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Workflows
                </Button>
                {pdfUrl && (
                  <Button variant="ghost" size="sm" asChild>
                    <a href={pdfUrl(selected.workflowSlug, selected.crSlug)}>
                      <Printer className="mr-2 h-4 w-4" />
                      Download PDF
                    </a>
                  </Button>
                )}
                {selectedDoc && (
                  <div className="mt-2 px-2">
                    <p className="text-xs text-muted-foreground">{selected.crTitle}</p>
                    <p className="text-sm font-semibold text-foreground">{selectedDoc.title}</p>
                  </div>
                )}
              </div>
              <Separator className="mx-3 my-2 w-auto" />
              <ScrollArea className="flex-1">
                <div className="space-y-1 px-3 pb-4">
                  {detailLoading ? (
                    <p className="px-5 py-2 text-sm text-muted-foreground">
                      Loading...
                    </p>
                  ) : (
                    detailDoc?.activities?.map((activity) => (
                      <div key={activity.id} className="flex items-center gap-0.5">
                        <button
                          onClick={() => setActiveActivityId(activity.id)}
                          className={`flex min-w-0 flex-1 items-center rounded-md px-3 py-2 text-left text-sm transition-colors ${
                            activeActivityId === activity.id
                              ? "bg-accent font-medium text-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-foreground"
                          }`}
                        >
                          {activity.name}
                        </button>
                        {pdfUrl && (
                          <a
                            href={pdfUrl(selected.workflowSlug, selected.crSlug, activity.id)}
                            title="Download module as PDF"
                            className="shrink-0 rounded-md p-1.5 text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
                          >
                            <Printer className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            className="flex w-6 shrink-0 items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Main content */}
        <div ref={contentRef} className="min-w-0 flex-1 overflow-auto">
          {detailLoading ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">Loading modules...</p>
            </div>
          ) : detailDoc && activeActivityId ? (
            <div className="px-6 py-6">
              <DocumentViewer
                document={detailDoc}
                activityId={activeActivityId}
              />
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
              <ListChecks className="mb-3 h-12 w-12 opacity-30" />
              <p>Select a module to view its contents</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // List mode: condition list + empty state
  return (
    <div className="flex h-full">
      {/* Left: collapsible condition list */}
      <div className={`flex shrink-0 border-r ${sidebarCollapsed ? "" : "w-80"}`}>
        {!sidebarCollapsed && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b px-5 py-4">
              <h1 className="text-xl font-semibold">Disease-Specific Workflows</h1>
              {allConditions.length > 0 && (
                <div className="relative mt-3">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
              )}
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4">
                {groupedFiltered.map(({ doc, conditions }) => (
                  <div key={doc.slug} className="mb-4 last:mb-0">
                    <div className="mb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {doc.title}
                      </p>
                      {doc.compliance_tags && doc.compliance_tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {doc.compliance_tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {conditions.map((entry) => (
                        <button
                          key={`${entry.workflowSlug}-${entry.crId}`}
                          onClick={() => handleSelectCondition(entry)}
                          className="flex w-full items-center justify-between rounded-lg border border-border bg-background px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent"
                        >
                          <span className="font-medium">{entry.crTitle}</span>
                          <ChevronRight className="ml-3 h-4 w-4 shrink-0 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className="flex w-6 shrink-0 items-start justify-center pt-4 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Right: empty state */}
      <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
        <ListChecks className="mb-3 h-12 w-12 opacity-30" />
        <p>Select a workflow to view</p>
        <p>its modules</p>
      </div>
    </div>
  )
}
